import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ScrambleText } from '@/components/ScrambleText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CODE_LINE_5 = 'artists.load("the_relative_gallery");render();await reveal();';

// Preferred ordering — known names first, everyone else follows in API order.
const ORDER = [
  'mr-relative', 'odesso', 'ivona-tau', 'arseniy-valter', 'nice-aunties',
];

export default function ArtistsSection() {
  const [artists, setArtists] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/artists`).then((r) => {
      const list = (r.data?.artists || [])
        .filter((a) => a.slug && a.show_on_main !== false)
        .map((a) => ({
          id: a.slug,
          slug: a.slug,
          name: (a.name || a.slug).toUpperCase(),
          image: a.pfp_url || null,
        }));
      list.sort((a, b) => {
        const ia = ORDER.indexOf(a.slug);
        const ib = ORDER.indexOf(b.slug);
        if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
        return a.name.localeCompare(b.name);
      });
      setArtists(list);
    }).catch(() => {});
  }, []);

  return (
    <section
      data-testid="artists-section"
      className="relative z-10 px-6 sm:px-10 py-20 lg:py-28"
    >
      <div className="overflow-hidden mb-16">
        <div className="code-divider-scroll whitespace-nowrap text-[10px] font-mono text-[#1a1a1a] select-none">
          {CODE_LINE_5} {CODE_LINE_5} {CODE_LINE_5} {CODE_LINE_5}
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7 }}
        >
          <ScrambleText
            text="MEET OUR ARTISTS"
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight uppercase text-white font-outfit block"
            as="h2"
            duration={800}
            distortOnHover
          />
          <div className="mb-14" />
        </motion.div>

        {/* Artist grid — full roster, pulled live */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-px border border-[#222222]">
          {artists.map((artist, i) => (
            <motion.div
              key={artist.id}
              data-testid={`artist-card-${artist.id}`}
              className="group relative border border-[#222222] overflow-hidden cursor-pointer aspect-[3/4] sm:aspect-square"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
            >
              <Link to={`/artists/${artist.slug}`} className="block w-full h-full" aria-label={`View ${artist.name} profile`}>
                {artist.image ? (
                  <img
                    src={artist.image}
                    alt={artist.name}
                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.03] group-hover:brightness-110"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#141414] to-[#050505]" />
                )}
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/10 transition-colors duration-500 flex items-end p-5 sm:p-6">
                  <div className="w-full flex items-end justify-between gap-3">
                    <h3 className="whitespace-pre-line leading-tight text-sm sm:text-base lg:text-lg font-outfit font-semibold text-white tracking-tight uppercase transition-opacity duration-400">
                      {artist.name}
                    </h3>
                    <span className="hidden sm:inline-flex text-[9px] font-mono uppercase tracking-[0.24em] text-[#90E0EF] opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                      VIEW →
                    </span>
                  </div>
                </div>
                {/* Subtle border glow on hover */}
                <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ boxShadow: 'inset 0 0 0 1px rgba(144,224,239,0.35)' }}
                />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
